import type { MetricItem } from "@shared/uiNew/MetricsStrip";
import type { CaseCardItem } from "@shared/uiNew/CaseCardsGrid";

// ─── Hero ─────────────────────────────────────────────────────────────────────

export const heroData = {
  pill: "Founded in 2017",
  subtitle:
    "Dashly is a customer communication platform that unites Marketing, Sales, and Support in one place — so no lead falls through the cracks.",
};

// ─── Metrics strip ────────────────────────────────────────────────────────────

export const metricsData: MetricItem[] = [
  { number: "2017", label: "year founded" },
  { number: "1B+", label: "messages delivered" },
  { number: "500+", label: "customer cases" },
  { number: "50+", label: "integrations" },
];

// ─── Story chapters ───────────────────────────────────────────────────────────

export const storyData = [
  {
    number: "01",
    title: "The problem we saw",
    text:
      "In 2017 we noticed that most companies were talking to customers like it was 2000. Marketing, Sales, and Support each had their own siloed tools — and customers got lost in the gaps between them.",
  },
  {
    number: "02",
    title: "What we built",
    text:
      "We created Dashly — a single platform where businesses engage visitors with live chat, qualify leads with AI, nurture prospects with automated campaigns, and support customers across every channel. One timeline. No silos.",
  },
  {
    number: "03",
    title: "Where we're going",
    text:
      "The future of customer communication is AI-native. Our AI agents engage, qualify, and book meetings 24/7 — with seamless handoff to humans when it matters. Every conversation becomes a revenue opportunity.",
  },
];

// ─── Values ───────────────────────────────────────────────────────────────────

export const valuesData = [
  {
    title: "Customer First",
    text: "Every decision starts with: how does this help our customers succeed?",
  },
  {
    title: "Data-Driven",
    text: "We measure everything. Opinions are welcome; data wins arguments.",
  },
  {
    title: "Move Fast",
    text: "Speed is a feature. We ship, learn, and iterate — then do it again.",
  },
  {
    title: "Think Long-Term",
    text: "We optimize for lifetime value and lasting partnerships, not quick wins.",
  },
  {
    title: "Radical Transparency",
    text: "We share context openly — with our team and with our customers.",
  },
  {
    title: "One Team",
    text: "Marketing, Sales, and Support are not silos here. We win together.",
  },
];

// ─── Cases ────────────────────────────────────────────────────────────────────

export const casesAbout: CaseCardItem[] = [
  {
    profit: "+82%",
    desc: "conversion from MQL to meeting",
    details:
      "AI agent instantly responded to inbound leads, qualified them via chat, and booked demos — cutting the sales cycle in half.",
    link: "/blog/",
    linkText: "Read case study",
  },
  {
    profit: "x2",
    desc: "qualified leads from same traffic",
    details:
      "Cascading trigger sequences based on 20+ behavioral signals guided customers through the funnel and doubled lead quality.",
    link: "/blog/",
    linkText: "Read case study",
  },
  {
    profit: "653%",
    desc: "ROMI from AI-driven campaigns",
    details:
      "Personalized AI messaging reached the right users at the right moment — turning dormant leads into paying customers.",
    link: "/blog/",
    linkText: "Read case study",
  },
];

// ─── Team ─────────────────────────────────────────────────────────────────────

export const teamData = [
  {
    initials: "DI",
    color: "#FF8119",
    name: "Dimitri Ive",
    role: "CEO & Co-founder",
    bio: "Serial entrepreneur obsessed with product-market fit. Leads Dashly's vision and company growth.",
    linkedin: "#",
  },
  {
    initials: "DT",
    color: "#6B4EFF",
    name: "Dima Turovski",
    role: "Head of Product",
    bio: "Product designer turned strategist. Leads Dashly's AI product roadmap and customer experience.",
    linkedin: "#",
  },
  {
    initials: "SI",
    color: "#1A1A1A",
    name: "Sergey Inkov",
    role: "CTO & Co-founder",
    bio: "Full-stack architect with 15+ years in high-load systems. Built Dashly's core chat and AI infrastructure.",
    linkedin: "#",
  },
];

// ─── Offices & Contacts ──────────────────────────────────────────────────────

export interface OfficeItem {
  city: string;
  country: string;
  address: string;
  mapQuery: string;
}

export const officesData: OfficeItem[] = [
  {
    city: "Wilmington",
    country: "United States",
    address: "3 Germany Dr, Unit 4, SUITE A#9999, Wilmington, DE 19804",
    mapQuery: "3+Germany+Dr+Unit+4+Wilmington+DE+19804",
  },
  {
    city: "Goulburn",
    country: "Australia",
    address: "4 Cooinda Cl, Goulburn NSW 2580",
    mapQuery: "4+Cooinda+Cl+Goulburn+NSW+2580",
  },
  {
    city: "Yerevan",
    country: "Armenia",
    address: "4 Pushkin St, Yerevan 0010",
    mapQuery: "4+Pushkin+St+Yerevan+0010+Armenia",
  },
];

export const contactEmail = "dmitrii@dashly.io";

// ─── FAQ ──────────────────────────────────────────────────────────────────────

export const faqAbout = [
  {
    "@type": "Question",
    name: "What is Dashly?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Dashly is a customer communication platform that helps Marketing, Sales, and Support teams engage, qualify, and convert visitors into customers — all in one place.",
    },
  },
  {
    "@type": "Question",
    name: "When was Dashly founded?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Dashly was founded in 2017. Since then we have delivered over 1 billion messages for 500+ companies worldwide.",
    },
  },
  {
    "@type": "Question",
    name: "How many integrations does Dashly support?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "Dashly connects with 50+ tools including CRMs, analytics platforms, messengers, and lead forms.",
    },
  },
  {
    "@type": "Question",
    name: "How can I join the Dashly team?",
    acceptedAnswer: {
      "@type": "Answer",
      text: "We are always looking for talented people who are passionate about customer communication and AI. Check our open positions or reach out to us directly.",
    },
  },
];
